import { gql, useApolloClient } from '@apollo/client';
import { useCallback } from 'react';
import { useSocketFeed } from '../../../store';
// eslint-disable-next-line import/no-internal-modules
import type { GetUntitledEventsQuery } from './__generated__/use-events';
import type { UntitledEvent } from './type';

const QUERY = gql`
query GetLiveAssets($untitled: Boolean) {
  getLiveAssets(untitled: $untitled) {
    assets {
      id
      manual_client_id
      created_at
      updated_at
      title
      description
      scheduled_time
      scheduled_end
      scheduled_event_id
      scheduled_event_password
      hidden
      was_ever_public
      mux_asset_id
      mux_playback_id
      mux_static_rendition_status
      mux_status
      is_live
      origin
      stream {
        mux_stream_id
        location_id
        id
        created_at
        client_id
      }
      stream_url
      download_url
      thumbnail_url
      archive_video_url
      duration
      stream_urls
    }
  }
}
`;

/*
 * The minimum spacing between refetches. The query always returns the whole untitled list from the
 * asset service, and it used to be refetched once per socket frame with no limit.
 */
const UNTITLED_THROTTLE_MS = 10000;

export const useUntitledEvents = (): {
  untitledEvents?: UntitledEvent[];
} => {
  const client = useApolloClient();

  /* `network-only` still writes the assets to the cache, where other screens may read them. */
  const fetchUntitled = useCallback(async (): Promise<UntitledEvent[]> => {
    const { data } = await client.query<GetUntitledEventsQuery>({
      query: QUERY,
      variables: { untitled: true },
      fetchPolicy: 'network-only'
    });

    return (data.getLiveAssets?.assets ?? []).filter((asset): asset is UntitledEvent => !!asset?.id);
  }, [client]);

  /*
   * Coming back to the Live tab renders the remembered list at once and refetches only if an untitled
   * change arrived meanwhile. The Apollo query it replaces served a stale cache on return.
   */
  const { rows, loading } = useSocketFeed({
    scope: 'live:untitled',
    cacheKey: 'untitled',
    enabled: true,
    fetch: fetchUntitled,
    topic: 'untitled',
    throttleMs: UNTITLED_THROTTLE_MS
  });

  /* Undefined while loading: the list tells "not loaded yet" apart from "no untitled streams". */
  return { untitledEvents: loading ? undefined : rows };
};
